import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'artist',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const payload = isRegister
        ? formData
        : { email: formData.email, password: formData.password };

      const response = await axios.post(`${API}${endpoint}`, payload);
      toast.success(isRegister ? 'Account created successfully!' : 'Welcome back!');
      onLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1709731191876-899e32264420?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-black/70" />
      </div>

      {/* Login Card */}
      <Card className="relative z-10 w-full max-w-md mx-4 p-8 bg-card/95 backdrop-blur-xl border-border/50 shadow-2xl">
        <div className="mb-8 text-center">
          <img src="/underplay-logo.png" alt="Underplay Music" className="w-48 mx-auto mb-4" />
          <h1 className="font-manrope font-extrabold text-3xl text-foreground mb-1" data-testid="login-title">
            HUG
          </h1>
          <p className="text-muted-foreground text-sm">Artist Management Platform</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {isRegister && (
            <div className="space-y-2">
              <Label htmlFor="name" className="font-manrope font-medium text-xs uppercase tracking-widest">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                data-testid="name-input"
                placeholder="Enter your name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required={isRegister}
                className="bg-input/50 border-border focus:border-primary focus:ring-primary/20 rounded-xl"
              />
            </div>
          )}

          {isRegister && (
            <div className="space-y-2">
              <Label htmlFor="role" className="font-manrope font-medium text-xs uppercase tracking-widest">
                Role
              </Label>
              <select
                id="role"
                data-testid="role-select"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                required={isRegister}
              >
                <option value="artist">Artist</option>
                <option value="manager">Manager</option>
              </select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="font-manrope font-medium text-xs uppercase tracking-widest">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="email-input"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="bg-input/50 border-border focus:border-primary focus:ring-primary/20 rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="font-manrope font-medium text-xs uppercase tracking-widest">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="password-input"
              placeholder="Enter your password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              className="bg-input/50 border-border focus:border-primary focus:ring-primary/20 rounded-xl"
            />
          </div>

          <Button
            type="submit"
            data-testid="auth-submit-button"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.3)] rounded-xl py-6 font-manrope font-semibold text-base"
          >
            {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            data-testid="toggle-auth-mode"
            onClick={() => setIsRegister(!isRegister)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Register"}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Login;